// [목적] auth 도메인 REST API 클라이언트 — GET /auth/oauth/:provider/authorize-url · POST /auth/refresh ·
//         POST /auth/logout · GET /auth/me · POST /auth/me/onboarding · GET /auth/csrf 매핑
// [이유] fe-be-wire-up Wave 0 INFRA-03 + oauth-login Wave 5 WC-FE-CLIENT-01 흡수 —
//         Auth 엔드포인트를 단일 모듈로 캡슐화하여 컴포넌트·훅이 fetch 를 직접 호출하지 않도록 강제
// [사이드 이팩트] BE AuthController 의 정확한 path/method/DTO 와 1:1 매핑.
//                 GET /auth/csrf 는 http.ts 가 403+CSRF 에러 시 자동 호출하므로 본 메서드는 테스트·수동 호출용.
//                 refresh() 는 http.ts 의 single-flight 로직이 자동 호출하므로 본 메서드는 테스트·수동 호출용.
// [수정 시 고려사항] meOutputSchema 는 apps/api/src/modules/auth/dto/me.dto.ts 에 정의됨 — packages/types 에 미이관 상태.
//                   onboardingInputSchema / onboardingOutputSchema 도 동일 (follow_up: packages/types 로 이관 권장).
//                   provider 파라미터는 BE providerParamSchema (kakao|naver|google) 와 동일하게 관리.

import { z } from 'zod';
import { fetchWithEnvelope } from './http.js';

// ─── Provider 타입 ─────────────────────────────────────────────────────────────

export type OAuthProvider = 'kakao' | 'naver' | 'google';

// ─── 응답 스키마 ───────────────────────────────────────────────────────────────

/**
 * [목적] GET /auth/oauth/:provider/authorize-url 응답 스키마
 * [이유] packages/types 미이관 — follow_up: packages/types/auth.ts 로 이관 권장
 * [사이드 이팩트] authorizeUrl 로 window.location.assign() 호출 후 OAuth provider 로 리다이렉트
 * [수정 시 고려사항] state 는 BE 가 Redis 에 저장한 CSRF nonce — FE 는 보관 불필요 (쿠키/세션 불필요)
 */
export const authorizeUrlResponseSchema = z.object({
  authorizeUrl: z.string().url(),
  state: z.string(),
});

export type AuthorizeUrlResponse = z.infer<typeof authorizeUrlResponseSchema>;

/**
 * [목적] GET /auth/me 응답 스키마 (BE me.dto.ts meOutputSchema 와 동일 구조)
 * [이유] packages/types 미이관 — follow_up: packages/types/auth.ts 로 이관 권장.
 *         BE meOutputSchema 와 구조 동기화 유지 필수.
 *         SID-BE-01: BE me.dto 에 primaryProvider 필드 추가됨 — FE 스키마 1:1 동기.
 * [사이드 이팩트] onboardedAt=null 시 AuthProvider 가 /onboarding 강제 redirect.
 *                 primaryProvider=null 이면 provider 미확인 상태 — 카카오 기본값 가정 금지.
 *                 SettingsPage/SettingsSubPages/Sidebar 에서 primaryProvider 소비 중.
 * [수정 시 고려사항] InterestEnum 는 BE onboarding.dto.ts 에서 z.enum(['nature','food','culture','active','cafe','healing']).
 *                   primaryProvider enum 값 추가 시 BE me.dto + FE 스키마 + providerLabel 헬퍼 동시 갱신.
 */
const interestEnum = z.enum(['nature', 'food', 'culture', 'active', 'cafe', 'healing']);

export const meOutputSchema = z
  .object({
    id: z.string().uuid(),
    handle: z.string(),
    displayName: z.string(),
    emailMasked: z.string().nullable(),
    profileImageUrl: z.string().url().nullable(),
    interests: z.array(interestEnum),
    marketingOptIn: z.boolean(),
    onboardedAt: z.string().datetime().nullable(),
    role: z.enum(['user', 'editor', 'admin']),
    createdAt: z.string().datetime(),
    primaryProvider: z.enum(['kakao', 'naver', 'google', 'apple']).nullable(),
  })
  .strict();

export type MeOutput = z.infer<typeof meOutputSchema>;

/**
 * [목적] POST /auth/me/onboarding 입력 스키마 (BE onboardingInputSchema 와 동일 구조)
 * [이유] packages/types 미이관 — follow_up: packages/types/auth.ts 로 이관 권장
 * [사이드 이팩트] 약관 동의(tos/privacy/location) 모두 true 필수 — BE zod refine 과 동일 검증
 * [수정 시 고려사항] profileImage.kind='custom' 은 v1.0 UI 에서 선택 불가
 */
const profileImageSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('kakao') }).strict(),
  z.object({ kind: z.literal('none') }).strict(),
  z.object({ kind: z.literal('custom'), assetId: z.string().uuid() }).strict(),
]);

export const onboardingInputSchema = z
  .object({
    displayName: z.string().min(1).max(20),
    profileImage: profileImageSchema,
    interests: z.array(interestEnum).min(1).max(6),
    consents: z
      .object({
        tos: z.boolean(),
        privacy: z.boolean(),
        location: z.boolean(),
        marketing: z.boolean(),
      })
      .strict()
      .refine((c) => c.tos && c.privacy && c.location, {
        message: 'tos, privacy, location consents are all required',
      }),
    consentVersion: z.string().min(1).max(50),
  })
  .strict();

export type OnboardingInput = z.infer<typeof onboardingInputSchema>;

/**
 * [목적] POST /auth/me/onboarding 응답 스키마 (BE onboardingOutputSchema 와 동일 구조)
 * [이유] packages/types 미이관 — follow_up 동일
 */
export const onboardingOutputSchema = z
  .object({
    user: z.object({
      id: z.string().uuid(),
      handle: z.string(),
      displayName: z.string(),
      profileImageUrl: z.string().url().nullable(),
      interests: z.array(interestEnum),
      marketingOptIn: z.boolean(),
      onboardedAt: z.string().datetime(),
    }),
  })
  .strict();

export type OnboardingOutput = z.infer<typeof onboardingOutputSchema>;

// ─── API 메서드 ────────────────────────────────────────────────────────────────

/**
 * [목적] GET /auth/oauth/:provider/authorize-url — provider OAuth 인가 URL 발급
 * [이유] INFRA-03: LoginPage 의 소셜 로그인 버튼 onClick 에서 호출 (oauth-login WC-FE-LOGIN-PATCH 흡수)
 * [사이드 이팩트] next 파라미터로 로그인 완료 후 redirect 경로 전달 가능
 * [수정 시 고려사항] 반환된 authorizeUrl 로 window.location.assign() 호출 — SPA 라우터가 아닌 전체 페이지 이동
 */
export async function getAuthorizeUrl(
  provider: OAuthProvider,
  next?: string,
): Promise<AuthorizeUrlResponse> {
  const params = next ? `?next=${encodeURIComponent(next)}` : '';
  const data = await fetchWithEnvelope<unknown>(`/auth/oauth/${provider}/authorize-url${params}`);
  return authorizeUrlResponseSchema.parse(data);
}

/**
 * [목적] GET /auth/csrf — CSRF 토큰 재발급 (gw_csrf 쿠키 갱신)
 * [이유] http.ts 가 403+CSRF 에러 시 자동 호출. 본 메서드는 단위 테스트·수동 호출용
 * [사이드 이팩트] JwtAuthGuard 필수 — 비로그인 상태에서 호출하면 401
 * [수정 시 고려사항] http.ts refetchCsrf() 와 동일 엔드포인트 호출
 */
export async function getCsrfToken(): Promise<{ csrfToken: string }> {
  // BE 는 { ok: true } 반환 + gw_csrf 쿠키 Set-Cookie
  await fetchWithEnvelope<unknown>('/auth/csrf');
  // 쿠키에서 현재 값 읽어 반환 (httpOnly=false 이므로 접근 가능)
  const match =
    typeof document !== 'undefined' ? document.cookie.match(/(?:^|;\s*)gw_csrf=([^;]+)/) : null;
  return { csrfToken: match?.[1] ? decodeURIComponent(match[1]) : '' };
}

/**
 * [목적] POST /auth/refresh — access token 갱신 (cookie-only 흐름)
 * [이유] http.ts single-flight 로직이 자동 호출. 본 메서드는 단위 테스트·수동 호출용
 * [사이드 이팩트] gw_at/gw_rt/gw_csrf 3종 쿠키 갱신 (Set-Cookie). SkipCsrf 적용 — CSRF 헤더 불필요
 * [수정 시 고려사항] http.ts doRefresh() 와 동일 엔드포인트 호출
 */
export async function refresh(): Promise<void> {
  await fetchWithEnvelope<unknown>('/auth/refresh', { method: 'POST' });
}

/**
 * [목적] POST /auth/logout — 세션 종료 + 쿠키 삭제
 * [이유] INFRA-03: useAuth().logout() 에서 호출
 * [사이드 이팩트] gw_at/gw_rt/gw_csrf 쿠키 삭제 (Max-Age=0). BE 가 refresh token revoke 처리
 * [수정 시 고려사항] CsrfGuard 적용 — X-CSRF-Token 헤더 필요. http.ts 가 자동 첨부
 */
export async function logout(): Promise<void> {
  await fetchWithEnvelope<unknown>('/auth/logout', { method: 'POST' });
}

/**
 * [목적] GET /auth/me — 현재 로그인 사용자 프로필 조회
 * [이유] INFRA-03: AuthProvider 의 staleTime=∞ useQuery 에서 호출 (oauth-login WC-FE-PROVIDER-01 흡수)
 * [사이드 이팩트] 비로그인 시 401 → ApiError throw. AuthProvider 가 status='guest' 로 처리
 * [수정 시 고려사항] JwtAuthGuard 필수. 응답은 meOutputSchema 로 파싱하여 환각 방지
 */
export async function me(): Promise<MeOutput> {
  const data = await fetchWithEnvelope<unknown>('/auth/me');
  return meOutputSchema.parse(data);
}

/**
 * [목적] POST /auth/me/onboarding — 온보딩 완료 (약관동의·닉네임·사진·취향 일괄 저장)
 * [이유] INFRA-03: OnboardingPage submit 에서 호출 (oauth-login WC-FE-ONBOARD-PATCH 흡수)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. users UPDATE + user_consents INSERT (BE 단일 트랜잭션)
 * [수정 시 고려사항] 입력은 onboardingInputSchema 로 FE 에서도 선검증. 응답은 onboardingOutputSchema 파싱
 */
export async function completeOnboarding(input: OnboardingInput): Promise<OnboardingOutput> {
  const data = await fetchWithEnvelope<unknown>('/auth/me/onboarding', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return onboardingOutputSchema.parse(data);
}
